import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Mail, Search, ChevronLeft, ChevronRight, RefreshCw, TrendingUp, Zap, CheckCircle2, XCircle } from 'lucide-react';
import { LoadingSpinner } from './core';
import { BulkBar, BulkCheck } from './shared';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_LABELS = {
  access_expired: ['Accès expiré', 'bg-red-500/15 text-red-400'],
  inactivity_relance: ['Relance inactivité', 'bg-amber-500/15 text-amber-400'],
  cart_relance: ['Relance panier', 'bg-orange-500/15 text-orange-400'],
  payment_failed: ['Échec paiement', 'bg-red-500/15 text-red-300'],
  bienvenue: ['Bienvenue', 'bg-green-500/15 text-green-400'],
  reset_password: ['Réinit. mot de passe', 'bg-blue-500/15 text-blue-400'],
  alerte_connexion: ['Alerte connexion', 'bg-purple-500/15 text-purple-400'],
  alerte_mdp: ['Alerte mot de passe', 'bg-purple-500/15 text-purple-300'],
  resiliation: ['Résiliation', 'bg-gray-500/15 text-gray-400'],
  support_admin: ['Support (admin)', 'bg-cyan-500/15 text-cyan-400'],
  support_recu: ['Support reçu', 'bg-cyan-500/15 text-cyan-300'],
  support_reponse: ['Support réponse', 'bg-cyan-500/15 text-cyan-400'],
  transactionnel: ['Transactionnel', 'bg-white/10 text-white/60'],
};
const kindLabel = (k) => (KIND_LABELS[k] || [k || '—', 'bg-white/10 text-white/60']);
const fmtDT = (iso) => { try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return '—'; } };

export function EmailsTab({ theme }) {
  const light = theme === 'light';
  const [view, setView] = useState('sent');
  const [stats, setStats] = useState(null);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [kind, setKind] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [openMail, setOpenMail] = useState(null);
  const [selectedM, setSelectedM] = useState([]);
  const [inbox, setInbox] = useState(null);
  const timer = useRef(null);
  const perPage = 25;

  const loadInbox = useCallback(() => {
    axios.get(`${API}/admin/inbox`, { withCredentials: true }).then(r => setInbox(r.data)).catch(() => setInbox({ items: [], total: 0, unread: 0 }));
  }, []);
  useEffect(() => { loadInbox(); }, [loadInbox]);

  useEffect(() => {
    axios.get(`${API}/admin/emails/stats`, { withCredentials: true }).then(r => setStats(r.data)).catch(() => setStats({}));
  }, []);

  const load = useCallback(() => {
    axios.get(`${API}/admin/emails/history`, { params: { page, per_page: perPage, kind, search }, withCredentials: true })
      .then(r => setData(r.data)).catch(() => setData({ items: [], total: 0, kinds: [] }));
  }, [page, kind, search]);
  useEffect(() => { load(); }, [load]);

  const onSearch = (v) => {
    setSearchInput(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { setPage(1); setSearch(v); }, 400);
  };

  const totalPages = Math.max(1, Math.ceil((data?.total || 0) / perPage));
  const cardCls = light ? 'bg-white border-gray-200' : 'bg-white/[0.03] border-white/10';
  const txtMain = light ? 'text-gray-900' : 'text-white';
  const txtSub = light ? 'text-gray-500' : 'text-white/45';
  const exp = stats?.access_expired || {};
  const inact = stats?.inactivity || {};

  return (
    <div data-testid="emails-tab">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className={`text-2xl font-bold ${txtMain}`}>Emails</h1>
          <p className={`text-xs mt-1 ${txtSub}`}>Historique de tous les emails envoyés aux abonnés</p>
        </div>
        {stats && (
          <span data-testid="auto-relance-badge" className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${stats.auto_relance_enabled ? 'bg-green-500/15 text-green-400' : light ? 'bg-gray-100 text-gray-500' : 'bg-white/8 text-white/50'}`}>
            <Zap size={12} />{stats.auto_relance_enabled ? 'Relance auto (30j d\'inactivité) : active' : 'Relance auto : active en production uniquement'}
          </span>
        )}
      </div>

      <div className={`flex gap-1 rounded-lg p-1 w-fit mb-4 ${light ? 'bg-gray-100' : 'bg-white/5'}`}>
        <button data-testid="emails-view-sent" onClick={() => setView('sent')}
          className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${view === 'sent' ? 'bg-[#E9641A] text-white' : light ? 'text-gray-500' : 'text-white/50'}`}>Envoyés</button>
        <button data-testid="emails-view-inbox" onClick={() => { setView('inbox'); loadInbox(); }}
          className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors inline-flex items-center gap-1.5 ${view === 'inbox' ? 'bg-[#E9641A] text-white' : light ? 'text-gray-500' : 'text-white/50'}`}>
          Boîte de réception
          {inbox?.unread > 0 && <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-red-500 text-white" data-testid="inbox-unread-badge">{inbox.unread}</span>}
        </button>
      </div>

      {openMail && <SentMailModal id={openMail} light={light} onClose={() => setOpenMail(null)} />}
      {view === 'inbox' ? <InboxView inbox={inbox} reload={loadInbox} light={light} /> : (<>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <div className={`rounded-xl border p-4 ${cardCls}`} data-testid="stat-resub">
          <div className="flex items-center gap-2 mb-1"><TrendingUp size={14} className="text-[#E9641A]" /><span className={`text-[11px] font-bold uppercase tracking-wider ${txtSub}`}>Réabonnements après email</span></div>
          <p className={`text-2xl font-bold ${txtMain}`}>{exp.resubscribed ?? '…'}<span className={`text-sm font-normal ${txtSub}`}> / {exp.emails_sent_to ?? '…'} relancés</span></p>
          <p className={`text-[11px] ${txtSub}`}>Abonnés expirés revenus après l'email « accès expiré »{exp.emails_sent_to ? ` · taux ${Math.round((exp.resubscribed / exp.emails_sent_to) * 100)}%` : ''}</p>
        </div>
        <div className={`rounded-xl border p-4 ${cardCls}`} data-testid="stat-inactivity">
          <div className="flex items-center gap-2 mb-1"><RefreshCw size={14} className="text-amber-400" /><span className={`text-[11px] font-bold uppercase tracking-wider ${txtSub}`}>Retours après relance inactivité</span></div>
          <p className={`text-2xl font-bold ${txtMain}`}>{inact.returned ?? '…'}<span className={`text-sm font-normal ${txtSub}`}> / {inact.emails_sent_to ?? '…'} relancés</span></p>
          <p className={`text-[11px] ${txtSub}`}>Abonnés reconnectés après l'email de relance{inact.emails_sent_to ? ` · taux ${Math.round((inact.returned / inact.emails_sent_to) * 100)}%` : ''}</p>
        </div>
        <div className={`rounded-xl border p-4 ${cardCls}`} data-testid="stat-total">
          <div className="flex items-center gap-2 mb-1"><Mail size={14} className="text-[#75D4D5]" /><span className={`text-[11px] font-bold uppercase tracking-wider ${txtSub}`}>Emails envoyés</span></div>
          <p className={`text-2xl font-bold ${txtMain}`}>{stats ? Object.values(stats.by_kind || {}).reduce((a, v) => a + (v.last30 || 0), 0) : '…'}<span className={`text-sm font-normal ${txtSub}`}> ces 30 jours</span></p>
          <p className={`text-[11px] ${txtSub}`}>{stats ? `${Object.values(stats.by_kind || {}).reduce((a, v) => a + (v.total || 0), 0)} au total` : ''}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className={`relative flex-1 min-w-[220px] max-w-sm`}>
          <Search size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${txtSub}`} />
          <input value={searchInput} onChange={e => onSearch(e.target.value)} placeholder="Rechercher un email…" data-testid="emails-search"
            className={`w-full pl-9 pr-3 py-2 rounded-lg text-sm border outline-none ${light ? 'bg-white border-gray-200 text-gray-900' : 'bg-white/5 border-white/10 text-white placeholder-white/30'}`} />
        </div>
        <select value={kind} onChange={e => { setKind(e.target.value); setPage(1); }} data-testid="emails-kind-filter"
          className={`text-sm rounded-lg px-3 py-2 border outline-none ${light ? 'bg-white border-gray-200 text-gray-700' : 'bg-[#1a0e2e] border-white/10 text-white/80'}`}>
          <option value="">Tous les types</option>
          {(data?.kinds || []).map(k => <option key={k} value={k}>{kindLabel(k)[0]}</option>)}
        </select>
        {data && <span className={`text-xs ${txtSub}`} data-testid="emails-total">{data.total} email{data.total > 1 ? 's' : ''}</span>}
      </div>

      {!data ? <LoadingSpinner /> : (
        <>
        <BulkBar items={data.items.filter(m => m.id)} selected={selectedM} setSelected={setSelectedM} kind="emails" light={light} onDone={() => { setSelectedM([]); load(); }} />
        <div className={`rounded-xl border overflow-hidden ${cardCls}`}>
          <div className={`hidden md:grid grid-cols-[24px_1fr_1.4fr_2fr_0.5fr] gap-3 px-5 py-3 text-[10px] font-bold uppercase tracking-wider border-b ${light ? 'text-gray-400 border-gray-100' : 'text-white/30 border-white/5'}`}>
            <span /><span>Date</span><span>Destinataire</span><span>Sujet / Type</span><span>Statut</span>
          </div>
          {data.items.length === 0 && <p className={`px-5 py-8 text-center text-sm ${txtSub}`}>Aucun email trouvé</p>}
          {data.items.map((m, i) => {
            const [lbl, cls] = kindLabel(m.kind);
            return (
              <div key={i} onClick={() => m.id && setOpenMail(m.id)} className={`grid grid-cols-1 md:grid-cols-[24px_1fr_1.4fr_2fr_0.5fr] gap-1 md:gap-3 px-5 py-3 items-center border-b last:border-0 ${m.id ? 'cursor-pointer' : ''} ${light ? 'border-gray-100 hover:bg-gray-50' : 'border-white/5 hover:bg-white/[0.03]'}`} data-testid={`email-row-${i}`}>
                <span>{m.id ? <BulkCheck id={m.id} selected={selectedM} setSelected={setSelectedM} light={light} /> : null}</span>
                <span className={`text-xs ${txtMain}`}>{fmtDT(m.sent_at)}</span>
                <span className={`text-xs truncate ${txtMain}`}>{m.email}</span>
                <div className="min-w-0 flex items-center gap-2">
                  <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold ${cls}`}>{lbl}</span>
                  <span className={`text-[11px] truncate ${txtSub}`}>{m.subject || '—'}</span>
                </div>
                <span className="text-xs">
                  {m.status === 'error'
                    ? <span className="inline-flex items-center gap-1 text-red-400"><XCircle size={12} />Échec</span>
                    : <span className="inline-flex items-center gap-1 text-green-500"><CheckCircle2 size={12} />Envoyé</span>}
                </span>
              </div>
            );
          })}
          {totalPages > 1 && (
            <div className={`flex items-center justify-between px-5 py-3 border-t ${light ? 'border-gray-100' : 'border-white/5'}`}>
              <span className={`text-xs ${txtSub}`}>Page {page} / {totalPages}</span>
              <div className="flex gap-1">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="emails-prev"
                  className={`p-1.5 rounded disabled:opacity-30 ${light ? 'hover:bg-gray-100 text-gray-600' : 'hover:bg-white/10 text-white/60'}`}><ChevronLeft size={14} /></button>
                <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="emails-next"
                  className={`p-1.5 rounded disabled:opacity-30 ${light ? 'hover:bg-gray-100 text-gray-600' : 'hover:bg-white/10 text-white/60'}`}><ChevronRight size={14} /></button>
              </div>
            </div>
          )}
        </div>
        </>
      )}
      </>)}
    </div>
  );
}

function SentMailModal({ id, light, onClose }) {
  const [mail, setMail] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/emails/${id}/content`, { withCredentials: true }).then(r => setMail(r.data)).catch(() => setMail({ error: true }));
  }, [id]);
  return (
    <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="sent-mail-modal">
      <div onClick={e => e.stopPropagation()} className={`w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-xl border flex flex-col ${light ? 'bg-white border-gray-200' : 'bg-[#1a0e2e] border-white/10'}`}>
        <div className={`flex items-start justify-between gap-3 px-5 py-4 border-b ${light ? 'border-gray-100' : 'border-white/10'}`}>
          <div className="min-w-0">
            <p className={`text-sm font-bold truncate ${light ? 'text-gray-900' : 'text-white'}`}>{mail?.subject || 'Chargement…'}</p>
            {mail && !mail.error && <p className={`text-[11px] mt-0.5 ${light ? 'text-gray-500' : 'text-white/45'}`}>À : {mail.email} · {fmtDT(mail.sent_at)} · {kindLabel(mail.kind)[0]}</p>}
          </div>
          <button onClick={onClose} data-testid="close-mail-modal" className={light ? 'text-gray-400 hover:text-gray-700' : 'text-white/40 hover:text-white'}><XCircle size={20} /></button>
        </div>
        <div className="flex-1 overflow-auto bg-white">
          {!mail ? <p className="p-6 text-xs text-gray-500">Chargement…</p>
            : mail.error ? <p className="p-6 text-xs text-red-500">Erreur de chargement</p>
            : mail.html ? <iframe title="email" srcDoc={mail.html} className="w-full h-[60vh] border-0" sandbox="" />
            : <p className="p-6 text-xs text-gray-500">Contenu non enregistré (email envoyé avant la mise à jour du système de traçage).</p>}
        </div>
      </div>
    </div>
  );
}

const INBOX_STATUS = {
  nouveau: ['Nouveau', 'bg-red-500/15 text-red-400'],
  lu: ['Lu', 'bg-white/10 text-white/50'],
  'répondu': ['Répondu', 'bg-green-500/15 text-green-400'],
  auto: ['Réponse auto IA', 'bg-purple-500/15 text-purple-400'],
};

function InboxView({ inbox, reload, light }) {
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const th = light ? 'text-gray-500' : 'text-white/45';
  const tx = light ? 'text-gray-900' : 'text-white';

  const openMsg = async (m) => {
    if (open === m.id) { setOpen(null); return; }
    setOpen(m.id); setDetail(null); setReply('');
    try {
      const { data } = await axios.get(`${API}/admin/inbox/${m.id}`, { withCredentials: true });
      setDetail(data); reload();
    } catch { setDetail({ error: true }); }
  };
  const sendReply = async (m) => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/inbox/${m.id}/reply`, { message: reply }, { withCredentials: true });
      setReply(''); setOpen(null); reload();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur lors de l\'envoi'); }
    setSending(false);
  };

  if (!inbox) return <LoadingSpinner />;
  return (
    <div data-testid="inbox-view">
      <div className={`rounded-xl border p-3 mb-4 flex items-center gap-2 ${light ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-[#75D4D5]/8 border-[#75D4D5]/20 text-[#75D4D5]'}`}>
        <Zap size={14} className="shrink-0" />
        <p className="text-[11px]">Les réponses des utilisateurs à vos emails arrivent ici automatiquement. L'agent IA répond seul quand il connaît la réponse (badge violet) — sinon le message reste en attente de votre réponse. <b>Configuration requise dans Resend : activer la réception (Receiving) sur mayaplus.fr + webhook « email.received » vers https://mayaplus.fr/api/webhooks/resend-inbound</b></p>
      </div>
      {inbox.items.length === 0 ? (
        <div className={`rounded-xl border p-10 text-center ${light ? 'bg-white border-gray-200' : 'bg-white/[0.03] border-white/10'}`}>
          <Mail size={26} className={`mx-auto mb-2 ${light ? 'text-gray-300' : 'text-white/15'}`} />
          <p className={`text-sm ${th}`}>Aucun message reçu pour le moment</p>
        </div>
      ) : (
        <div className={`rounded-xl border overflow-hidden ${light ? 'bg-white border-gray-200' : 'bg-white/[0.03] border-white/10'}`}>
          {inbox.items.map(m => {
            const [lbl, cls] = INBOX_STATUS[m.status] || [m.status, 'bg-white/10 text-white/50'];
            return (
              <div key={m.id} className={`border-b last:border-0 ${light ? 'border-gray-100' : 'border-white/5'}`}>
                <div onClick={() => openMsg(m)} className={`flex items-center justify-between gap-3 px-5 py-3.5 cursor-pointer ${light ? 'hover:bg-gray-50' : 'hover:bg-white/[0.03]'}`} data-testid={`inbox-row-${m.id}`}>
                  <div className="min-w-0">
                    <p className={`text-xs truncate ${m.status === 'nouveau' ? `font-bold ${tx}` : `font-semibold ${tx}`}`}>{m.subject}</p>
                    <p className={`text-[10px] truncate ${th}`}>{m.from_email} · {fmtDT(m.received_at)}</p>
                  </div>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0 ${cls}`}>{lbl}</span>
                </div>
                {open === m.id && (
                  <div className={`px-5 pb-4 ${light ? 'bg-gray-50/60' : 'bg-white/[0.02]'}`} data-testid={`inbox-detail-${m.id}`}>
                    {!detail ? <p className={`text-xs py-3 ${th}`}>Chargement…</p> : detail.error ? <p className="text-xs py-3 text-red-400">Erreur</p> : (<>
                      <div className={`text-xs whitespace-pre-wrap rounded-lg p-3 my-2 ${light ? 'bg-white border border-gray-200 text-gray-800' : 'bg-white/5 text-white/80'}`}>{detail.text || '(contenu HTML uniquement)'}</div>
                      {(detail.replies || []).map((r, i) => (
                        <div key={i} className={`text-xs rounded-lg p-3 mb-2 ml-6 ${r.by === 'Agent IA' ? 'bg-purple-500/10 border border-purple-500/20' : light ? 'bg-green-50 border border-green-200' : 'bg-green-500/10 border border-green-500/20'}`}>
                          <p className={`text-[10px] font-bold mb-1 ${r.by === 'Agent IA' ? 'text-purple-400' : 'text-green-500'}`}>{r.by} · {fmtDT(r.sent_at)}</p>
                          <p className={`whitespace-pre-wrap ${light ? 'text-gray-700' : 'text-white/75'}`}>{r.message}</p>
                        </div>
                      ))}
                      <div className="flex gap-2 mt-2">
                        <textarea value={reply} onChange={e => setReply(e.target.value)} rows={2} placeholder="Votre réponse… (envoyée par email à l'utilisateur)"
                          data-testid={`inbox-reply-input-${m.id}`}
                          className={`flex-1 text-xs rounded-lg p-2.5 border outline-none resize-none ${light ? 'bg-white border-gray-200 text-gray-900' : 'bg-white/5 border-white/10 text-white placeholder-white/30'}`} />
                        <button disabled={sending || !reply.trim()} onClick={() => sendReply(m)} data-testid={`inbox-reply-send-${m.id}`}
                          className="px-4 rounded-lg text-xs font-bold bg-[#E9641A] text-white hover:bg-[#d4570f] disabled:opacity-40 transition-colors">
                          {sending ? 'Envoi…' : 'Répondre'}
                        </button>
                      </div>
                    </>)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
